import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useSSE } from "@/hooks/useSSE";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KPITile } from "@/components/KPITile";
import { StatusPill } from "@/components/StatusPill";
import { Card } from "@/components/ui/card";
import { Briefcase, CheckCircle2, AlertTriangle, Clock, PackageX, Truck, ArrowRight, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const STATUS_COLORS = {
  scheduled: "#71717a",
  in_progress: "#3b82f6",
  at_risk: "#f59e0b",
  delayed: "#ef4444",
  completed: "#10b981",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [livePulse, setLivePulse] = useState(0);

  const refresh = useCallback(() => {
    api.get("/dashboard/kpis").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Real-time updates via SSE
  useSSE(useCallback((ev) => {
    if (ev.type === "execution.created") {
      toast.message("Execution update received", { description: `${ev.campaign_id} · ${ev.pct}% achieved` });
      refresh();
      setLivePulse((p) => p + 1);
    } else if (ev.type === "notification.created") {
      toast.warning("New alert", { description: `${ev.campaign_id} flagged ${ev.level}` });
      refresh();
    } else if (ev.type === "campaign.created" || ev.type === "campaign.updated" || ev.type === "campaign.deleted" || ev.type === "campaign.bulk_deleted") {
      refresh();
    }
  }, [refresh]), ["execution.created", "notification.created", "campaign.created", "campaign.updated", "campaign.deleted", "campaign.bulk_deleted"]);

  if (loading || !data) {
    return <div className="text-sm text-zinc-500" data-testid="dashboard-loading">Loading control tower…</div>;
  }
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Executive Overview</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Operations control tower</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-zinc-500" data-testid="sse-indicator">
            <span className={`h-2 w-2 rounded-full ${livePulse > 0 ? "bg-emerald-500 animate-pulse" : "bg-emerald-500"}`} />
            <span className="font-mono">live</span>
          </div>
          <p className="text-xs text-zinc-500 font-mono">Last refresh: {new Date().toLocaleTimeString()}</p>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <KPITile testId="kpi-active-campaigns" label="Active Campaigns" value={t.active_campaigns} hint={`${t.total_campaigns} total`} icon={Briefcase} />
        <KPITile testId="kpi-installations" label="Installations" value={`${t.total_completed_installations}/${t.total_planned_installations}`} hint="Completed / Planned" icon={CheckCircle2} accent="green" />
        <KPITile testId="kpi-sla" label="SLA Adherence" value={`${t.sla_adherence_percent}%`} hint="On-time installations" icon={TrendingUp} accent={t.sla_adherence_percent >= 80 ? "green" : t.sla_adherence_percent >= 60 ? "amber" : "red"} />
        <KPITile testId="kpi-pending-onsite" label="Pending On-site" value={t.pending_onsite} icon={Clock} accent={t.pending_onsite > 20 ? "amber" : "default"} />
        <KPITile testId="kpi-pending-delivery" label="Pending Delivery" value={t.pending_delivery} icon={Truck} accent={t.pending_delivery > 15 ? "amber" : "default"} />
      </div>

      {/* Risk strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-center gap-3" data-testid="risk-card-at-risk">
          <AlertTriangle className="h-5 w-5 text-amber-700" />
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-amber-800">At-risk campaigns</div>
            <div className="font-display text-2xl font-bold text-amber-900">{t.at_risk_count}</div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center gap-3" data-testid="risk-card-delayed">
          <PackageX className="h-5 w-5 text-red-700" />
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-red-800">Delayed campaigns</div>
            <div className="font-display text-2xl font-bold text-red-900">{t.delayed_count}</div>
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-center gap-3" data-testid="risk-card-completed">
          <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-emerald-800">Completed</div>
            <div className="font-display text-2xl font-bold text-emerald-900">{t.completed_campaigns}</div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center gap-3" data-testid="risk-card-active">
          <Briefcase className="h-5 w-5 text-blue-700" />
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-blue-800">Live now</div>
            <div className="font-display text-2xl font-bold text-blue-900">{t.active_campaigns}</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5" data-testid="chart-daily-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold">Daily Installations — Last 14 Days</h3>
              <p className="text-xs text-zinc-500">Planned vs Completed</p>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily_trend} margin={{ left: -10 }}>
                <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="planned" stroke="#71717a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="completed" stroke="#0055FF" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-testid="chart-status">
          <h3 className="font-display text-lg font-semibold">Status Mix</h3>
          <p className="text-xs text-zinc-500 mb-4">All campaigns</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.status_distribution} dataKey="count" nameKey="status" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {data.status_distribution.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || "#71717a"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {data.status_distribution.map((s) => (
              <div key={s.status} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 capitalize">
                  <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[s.status] || "#71717a" }} />
                  {s.status.replace("_", " ")}
                </span>
                <span className="font-mono font-semibold">{s.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* City summary */}
      <Card className="p-5" data-testid="city-summary-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold">City / Zone Performance</h3>
            <p className="text-xs text-zinc-500">Installations completed vs target by city</p>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.city_summary} margin={{ left: -10 }}>
              <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" />
              <XAxis dataKey="city" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="target" fill="#a1a1aa" radius={[4, 4, 0, 0]} />
              <Bar dataKey="completed" fill="#0055FF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* At-risk list */}
      <Card className="p-5" data-testid="at-risk-list">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-lg font-semibold">Campaigns needing attention</h3>
            <p className="text-xs text-zinc-500">At-risk and delayed</p>
          </div>
          <Link to="/campaigns" className="text-xs text-zinc-700 hover:text-zinc-950 font-medium inline-flex items-center gap-1" data-testid="view-all-campaigns-link">
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <tr>
                <th className="text-left py-2 px-3 font-semibold">Campaign ID</th>
                <th className="text-left py-2 px-3 font-semibold">Name</th>
                <th className="text-left py-2 px-3 font-semibold">City</th>
                <th className="text-left py-2 px-3 font-semibold">Progress</th>
                <th className="text-left py-2 px-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.at_risk_campaigns.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-zinc-500 text-xs">All campaigns on track 🎯</td></tr>
              )}
              {data.at_risk_campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`at-risk-row-${c.campaign_id}`}>
                  <td className="py-2.5 px-3 font-mono text-xs">{c.campaign_id}</td>
                  <td className="py-2.5 px-3">
                    <Link to={`/campaigns/${c.campaign_id}`} className="hover:underline font-medium">{c.campaign_name}</Link>
                  </td>
                  <td className="py-2.5 px-3 text-zinc-600">{c.city}</td>
                  <td className="py-2.5 px-3 text-zinc-600 font-mono text-xs">{c.completed}/{c.target}</td>
                  <td className="py-2.5 px-3"><StatusPill status={c.health} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
